import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import SendComposeModal from '../../components/common/SendComposeModal';
import GuestList from '../../components/common/GuestList';
import PersonAvatar from '../../components/common/PersonAvatar';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';

/* Who is coming, who is not, and who has not said.

   The third of those is the one nothing in the platform could answer before, and it is the whole
   reason this screen exists: "not registered" is the roster minus the people who said yes minus
   the people who said no — the first negation the platform computes. It is also what the reminder
   button sends to.

   Seven segments, always with all seven counts, because the counts ARE the report: 40 invited,
   22 coming, 3 not, 15 silent, and afterwards 19 who actually showed up.

   ★ The roster tag filter ("the Antwerp office") narrows the audience, not the display. Reminding
   half the company by accident is not an undo-able mistake. */

// ★ The two SURVEY segments are deliberately absent, though the API serves them: they only mean
// anything with a survey in context, and this screen has none — "survey pending" with no survey
// resolves to everyone who registered, which is a confident wrong answer. They live on the host
// screen, beside the survey they are about, where the reminder button sends to them.
const SEGMENTS = ['registered', 'not_registered', 'declined', 'attended', 'no_show'];

// The three the admin steers by, first. The rest are a report, and a report can wait a row.
const PRIMARY = ['registered', 'not_registered', 'declined'];

// A stable colour per tag name, so "Antwerp office" is the same blue every time the screen
// loads and the eye can group the pills without reading them. Hashed rather than indexed on
// purpose: a tag added alphabetically above another one must not repaint the whole row.
const TAG_COLOURS = 8;
const tagColour = (tag) => {
  let h = 0;
  for (let i = 0; i < tag.length; i += 1) h = (h * 31 + tag.charCodeAt(i)) % 1000003;
  return `ce-tag--c${h % TAG_COLOURS}`;
};

const CompanyEventPeople = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [segment, setSegment] = useState('registered');
  const [tags, setTags] = useState([]);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  // What the composer is open for: {kind, ids, name} or null. An empty `ids` means the whole
  // segment; a single id is the personal send, and `name` is there only to title the modal
  // with it — so the host can see at a glance that this one goes to one person.
  const [composing, setComposing] = useState(null);
  // ★ The "we are sending this outside" switch, and it defaults to ON. The sheet is most often
  // opened to read it here — the ticked fields are what the host decided the organiser needs —
  // and a download that silently withheld half of them would read as a bug. Turning it off drops
  // exactly the fields the catalogue marks personal (a date of birth, a mobile number); name and
  // email always stay, because a participant list without those is not a list, and the ANSWERS
  // are never touched: those were given in reply to this organiser's own questions.
  const [personal, setPersonal] = useState(true);
  // The guest list is a second view of this screen rather than a second screen: it is the
  // same question (who is this event for) one step earlier. ?tab=invites opens it directly,
  // which is where the host screen's "manage guest list" button lands.
  const [tab, setTab] = useState(
    () => (new URLSearchParams(window.location.search).get('tab') === 'invites'
      ? 'invites' : 'audience'));

  const query = useCallback((extra = '') => {
    const parts = [`segment=${encodeURIComponent(segment)}`];
    tags.forEach((tag) => parts.push(`tag=${encodeURIComponent(tag)}`));
    return `?${parts.join('&')}${extra}`;
  }, [segment, tags]);

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/audience${query()}`)
      .then(setData)
      .catch(() => navigate('/programmes'));
  }, [eventId, query, navigate]);

  useEffect(() => { load(); }, [load]);

  const setStatus = (partnerId, status) => {
    if (busy) return;
    setBusy(true);
    setNotice(null);
    apiRequest('POST', `/company/event/${eventId}/register`,
               { partner_id: partnerId, status })
      .then(load)
      .catch((err) => setNotice(apiErrorText(t, err)))
      .finally(() => setBusy(false));
  };

  // Both sends open the same composer. "Inform them" is its own message, not a repeat of the
  // invitation: somebody who answered a fortnight ago reads a repeated invitation as though
  // their answer went missing. What the composer changed is that the host now READS it first —
  // there is no confirm step behind the modal, because the modal is the confirmation.
  const sent = (res) => {
    setNotice(t('sendSummary', { mailed: res.mailed || 0, n: res.notified || 0 }));
    load();
  };

  const exportXlsx = () => {
    // Same-origin, and the route sets Content-Disposition, so the browser downloads and stays
    // on the page. The JWT rides on the cookie exactly as it does for every other call.
    //
    // ★ The sheet follows the tabs: the same segment and the same roster tags the list on screen
    // resolves to. "Export people from list below" is the label because it is literally that
    // list, with every answer they have given beside their name.
    const a = document.createElement('a');
    a.href = `/api/company/event/${eventId}/attendees.xlsx${query(personal ? '' : '&personal=0')}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const toggleTag = (tag) => setTags((list) =>
    (list.includes(tag) ? list.filter((x) => x !== tag) : [...list, tag]));

  const counts = (data && data.counts) || {};
  const people = (data && data.people) || [];
  const allTags = (data && data.all_tags) || [];
  // "Not registered" is the only segment here where an answer is still owed. Chasing unanswered
  // SURVEYS is the host screen's button, because it needs to know which survey.
  const canRemind = segment === 'not_registered';
  // The people who said yes (and, after the day, the ones who turned up) are the ones there is
  // something to inform ABOUT.
  const canInform = segment === 'registered' || segment === 'attended';

  return (
    <Base activeNav="home">
      {composing && (
        <SendComposeModal
          eventId={eventId}
          kind={composing.kind}
          segment={segment}
          tags={tags}
          ids={composing.ids}
          title={composing.name
            ? t(composing.kind === 'remind' ? 'remindOneTitle' : 'informOneTitle',
                { name: composing.name })
            : t(composing.kind === 'remind' ? 'remindThem' : 'informThem')}
          sendPath={`/company/event/${eventId}/${composing.kind}`}
          onClose={() => setComposing(null)}
          onSent={sent}
        />
      )}
      <Card>
        <Back to={`/company-event/${eventId}/edit`} />
        <Title my="1" className="mt-3">
          <Icon name="user-check" theme="primary" className="me-2" />
          {t('whoIsComing')}
        </Title>

        {/* Who is invited, and who has answered — two views of one question. */}
        <div className="ce-tabs ce-tabs--major">
          <button type="button" className={`ce-tab ${tab === 'audience' ? 'is-on' : ''}`}
                  onClick={() => setTab('audience')}>
            {t('whoIsComing')}
          </button>
          <button type="button" className={`ce-tab ${tab === 'invites' ? 'is-on' : ''}`}
                  onClick={() => setTab('invites')}>
            {t('guestList')}
          </button>
        </div>

        {tab === 'invites' && <GuestList eventId={eventId} onChanged={load} />}

        {tab === 'audience' && (
        <>
        <div className="ce-tabs">
          {PRIMARY.map((s) => (
            <button
              key={s}
              type="button"
              className={`ce-tab ${segment === s ? 'is-on' : ''}`}
              onClick={() => setSegment(s)}
            >
              {t(`segment_${s}`)}
              <span className="ce-tab__n">{counts[s] != null ? counts[s] : '–'}</span>
            </button>
          ))}
        </div>
        <div className="ce-tabs">
          {SEGMENTS.filter((s) => !PRIMARY.includes(s)).map((s) => (
            <button
              key={s}
              type="button"
              className={`ce-tab ${segment === s ? 'is-on' : ''}`}
              onClick={() => setSegment(s)}
            >
              {t(`segment_${s}`)}
              <span className="ce-tab__n">{counts[s] != null ? counts[s] : '–'}</span>
            </button>
          ))}
        </div>

        {allTags.length > 0 && (
          <>
            <p className="bravos-cal__label">{t('filterByTag')}</p>
            <div className="ce-tabs">
              {allTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  className={`ce-tab ce-tag ${tagColour(tag)} ${tags.includes(tag) ? 'is-on' : ''}`}
                  onClick={() => toggleTag(tag)}
                >
                  {tag}
                </button>
              ))}
            </div>
          </>
        )}

        <div className="bravos-form__row mt-2">
          {/* ★ Every label here names the LIST BELOW rather than an unnamed "them". These three
              buttons act on whatever the segment tabs and the tag filter currently resolve to,
              and "Remind them" left the host to work out who "them" was from two rows away. */}
          {canRemind && (
            <button
              type="button"
              className="bravos-copy"
              onClick={() => setComposing({ kind: 'remind', ids: [] })}
              disabled={busy || people.length === 0}
            >
              <Icon name="bell" /> {t('remindListBelow')}
            </button>
          )}
          {/* Only where an answer already exists — informing people who never said yes is what
              the invitation and the reminder are for. */}
          {canInform && (
            <button
              type="button"
              className="bravos-copy"
              onClick={() => setComposing({ kind: 'inform', ids: [] })}
              disabled={busy || people.length === 0}
            >
              <Icon name="bell" /> {t('informListBelow')}
            </button>
          )}
          <button type="button" className="bravos-copy" onClick={exportXlsx}>
            <Icon name="file-download" /> {t('exportListBelow')}
          </button>
          <label className="ce-check ce-check--inline">
            <input
              type="checkbox"
              checked={personal}
              onChange={(e) => setPersonal(e.target.checked)}
            />
            <span>
              {t('includePersonalData')}
              <span className="ce-check__hint">{t('includePersonalDataHint')}</span>
            </span>
          </label>
        </div>
        {notice && <p className="bravos-cal__notice">{notice}</p>}
        </>
        )}
      </Card>

      {tab === 'audience' && (
      <Card>
        {data === null ? (
          <span className="auth__spinner" />
        ) : people.length === 0 ? (
          <p className="ce-note">{t('nobodyHere')}</p>
        ) : (
          people.map((p) => (
            <div key={p.id} className="ce-person ce-person--audience">
              <PersonAvatar person={p} />
              <span className="ce-person__body">
                <span className="ce-person__name">{p.name || p.email}</span>
                <span className="ce-person__email">{p.email}</span>
              </span>
              <span className="ce-person__acts">
                {/* One person, not the segment. The same composer, the same two channels and
                    the same server-side audience resolution — `ids` only NARROWS, so this can
                    never reach anybody who is not already in the list on screen. It exists
                    because chasing one straggler should not mail the other twenty-nine. */}
                {(canRemind || canInform) && (
                  <button
                    type="button"
                    className="ce-person__act ce-person__act--send"
                    onClick={() => setComposing({
                      kind: canRemind ? 'remind' : 'inform',
                      ids: [p.id],
                      name: p.name || p.email,
                    })}
                    disabled={busy}
                  >
                    <Icon name="paper-plane" /> {t(canRemind ? 'remindOne' : 'informOne')}
                  </button>
                )}
                {/* Answering on someone's behalf — "she told me in the corridor". They still get
                    the confirmation mail, which is how they find out they are expected. */}
                {segment !== 'registered' && segment !== 'attended' && (
                  <button
                    type="button"
                    className="ce-person__act"
                    onClick={() => setStatus(p.id, 'going')}
                    disabled={busy}
                  >
                    {t('markComing')}
                  </button>
                )}
                {segment === 'registered' && (
                  <button
                    type="button"
                    className="ce-person__act"
                    onClick={() => setStatus(p.id, 'not_going')}
                    disabled={busy}
                  >
                    {t('markNotComing')}
                  </button>
                )}
              </span>
            </div>
          ))
        )}
      </Card>
      )}
    </Base>
  );
};

export default CompanyEventPeople;
